'use client';
import { jsxs, jsx } from '@emotion/react/jsx-runtime';
import { forwardRef, useRef, useCallback } from 'react';
import { Transition, Overlay } from '@s7/base';
import { useMergedRefs } from '@s7/hooks';
import { useProps, cssVars } from '@s7/styles';
import { IDENT, defaultProps, safeWindow } from './SwipeableDrawer.contract.js';
import useStyles, { HANDLE_SIZE } from './SwipeableDrawer.styles.js';
import { useDrawerSwipeGesture } from './useDrawerSwipeGesture/useDrawerSwipeGesture.js';
import { Modal } from '../Modal/Modal.js';

function _extends() {
    _extends = Object.assign || function(target) {
        for(var i = 1; i < arguments.length; i++){
            var source = arguments[i];
            for(var key in source){
                if (Object.prototype.hasOwnProperty.call(source, key)) {
                    target[key] = source[key];
                }
            }
        }
        return target;
    };
    return _extends.apply(this, arguments);
}
function _object_without_properties_loose(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
const computeHeight = (ref)=>{
    var _ref_current;
    return ((_ref_current = ref.current) == null ? void 0 : _ref_current.getBoundingClientRect().height) || 0;
};
const SwipeableDrawer = /*#__PURE__*/ forwardRef((props, ref)=>{
    const _useProps = useProps(IDENT, props, defaultProps), [{ snapPoint, opened, disableSwipe, radius, /// Слоты
    title, children, /// Backdrop
    backdrop, className, /// Refs
    contentRef, headerRef, titleRef, bodyRef, onClose }, sxs] = _useProps, others = _object_without_properties_loose(_useProps[0], [
        "snapPoint",
        "opened",
        "disableSwipe",
        "radius",
        "title",
        "children",
        "backdrop",
        "className",
        "contentRef",
        "headerRef",
        "titleRef",
        "bodyRef",
        "onClose"
    ]);
    const [classes, cx] = useStyles({
        radius
    }, sxs, IDENT);
    const innerHeaderRef = useRef();
    const innerBodyRef = useRef();
    const headerRefs = useMergedRefs(innerHeaderRef, headerRef);
    const bodyRefs = useMergedRefs(innerBodyRef, bodyRef);
    const getAutoHeight = useCallback(()=>{
        const headerHeight = computeHeight(innerHeaderRef);
        const bodyHeight = computeHeight(innerBodyRef);
        return safeWindow.innerHeight - (headerHeight + bodyHeight + HANDLE_SIZE);
    }, [
        safeWindow,
        innerHeaderRef,
        innerBodyRef
    ]);
    const [dragHandleRef, dragging, [contentStyle, transitionProps], [overlayOpacity, getOverlayStyle]] = useDrawerSwipeGesture(snapPoint, opened, disableSwipe, getAutoHeight, onClose);
    const contentHeight = `calc(100vh - ${computeHeight(innerHeaderRef) + 160}px - env(safe-area-inset-bottom))`;
    const onOverlayClick = ()=>{
        if (others.closeOnClickOutside) {
            onClose();
        }
    };
    return /*#__PURE__*/ jsxs(Modal, _extends({}, others, {
        ref: ref,
        className: cx(classes.root, className),
        opened: opened,
        style: cssVars({
            contentHeight
        }),
        transitionProps: {
            transition: 'slide-up'
        },
        onClose: onClose,
        children: [
            backdrop && /*#__PURE__*/ jsx(Transition, {
                duration: 400,
                mounted: opened,
                transition: "fade",
                children: (transitionStyles)=>/*#__PURE__*/ jsx(Overlay, {
                        className: classes.backdrop,
                        opacity: overlayOpacity,
                        style: getOverlayStyle(transitionStyles),
                        zIndex: others.zIndex,
                        fixed: true,
                        onClick: onOverlayClick
                    })
            }),
            /*#__PURE__*/ jsxs(Modal.Content, {
                ref: contentRef,
                classNames: {
                    content: classes.content,
                    inner: classes.inner
                },
                style: contentStyle,
                transitionProps: transitionProps,
                children: [
                    /*#__PURE__*/ jsxs("div", {
                        ref: dragHandleRef,
                        className: classes.draggable,
                        children: [
                            title && /*#__PURE__*/ jsx(Modal.Header, {
                                ref: headerRefs,
                                className: classes.header,
                                children: /*#__PURE__*/ jsx(Modal.Title, {
                                    ref: titleRef,
                                    className: classes.title,
                                    children: title
                                })
                            }),
                            /*#__PURE__*/ jsx("div", {
                                className: classes.dragHandleBar,
                                children: /*#__PURE__*/ jsx("div", {
                                    className: classes.dragHandle,
                                    "data-active": !disableSwipe && dragging
                                })
                            })
                        ]
                    }),
                    /*#__PURE__*/ jsx(Modal.Body, {
                        ref: bodyRefs,
                        className: classes.body,
                        children: children
                    })
                ]
            })
        ]
    }));
});
SwipeableDrawer.displayName = IDENT;
var SwipeableDrawer$1 = SwipeableDrawer;

export { SwipeableDrawer, SwipeableDrawer$1 as default };
//# sourceMappingURL=SwipeableDrawer.js.map
